/**
 * OAuth Callback Component
 * Handles OAuth responses from Metricool and communicates with parent window
 */

import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const OAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing connection...');
  const [platform, setPlatform] = useState<string>('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const metricoolToken = urlParams.get('metricoolToken');
        const platformParam = urlParams.get('platform');
        const brandId = urlParams.get('brandId');
        
        // Extract platform from state or platform parameter
        const platformName = platformParam || state || 'unknown';
        setPlatform(platformName);
        
        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }
        
        if (metricoolToken) {
          // Metricool OAuth flow completed successfully
          setMessage(`Successfully connected ${platformName}!`);
          setStatus('success');
          
          // Notify parent window of success
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_SUCCESS',
              platform: platformName,
              token: metricoolToken,
              brandId: brandId
            }, window.location.origin);
          }
          
          // Auto-close after 2 seconds
          setTimeout(() => {
            window.close();
          }, 2000);
          
        } else if (code) {
          // Standard OAuth code flow - exchange code for token
          setMessage('Exchanging authorization code...');
          
          const response = await fetch(`/api/oauth/${platformName}/callback`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code,
              state,
              redirectUri: window.location.origin + '/oauth/callback'
            }),
          });
          
          if (!response.ok) {
            throw new Error(`Failed to exchange code: ${response.status}`);
          }
          
          const result = await response.json();
          
          if (result.success) {
            setMessage(`Successfully connected ${platformName}!`);
            setStatus('success');
            
            // Notify parent window of success
            if (window.opener) {
              window.opener.postMessage({
                type: 'OAUTH_SUCCESS',
                platform: platformName,
                token: result.token
              }, window.location.origin);
            }
            
            // Auto-close after 2 seconds
            setTimeout(() => {
              window.close();
            }, 2000);
          } else {
            throw new Error(result.error || 'Unknown error occurred');
          }
          
        } else {
          throw new Error('No authorization code or token received');
        }
        
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Connection failed');
        
        // Notify parent window of error
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_ERROR',
            platform: platform,
            error: error instanceof Error ? error.message : 'Unknown error'
          }, window.location.origin);
        }
      }
    };

    handleOAuthCallback();
  }, []);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleClose = () => {
    window.close();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-500" />
            <h2 className="text-xl font-semibold mb-2">Connecting Account</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-semibold mb-2 text-green-700">Connection Successful!</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">This window will close automatically...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold mb-2 text-red-700">Connection Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={handleRetry} variant="outline">
                Try Again
              </Button>
              <Button onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback; 